import { useEffect, useState } from "react";
import {
  getAdminAttorneyAccess,
  approveAttorneyAccess,
  rejectAttorneyAccess,
  revokeAttorneyAccess,
} from "../api/attorneyAccess";
import AdminNav from "../components/AdminNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

export default function AdminAttorneyAccess() {
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");

  const loadAccess = async () => {
    setLoading(true);

    try {
      const res = await getAdminAttorneyAccess();
      setAccesses(res.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load attorney access."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccess();
  }, []);

  const handleApprove = async (id) => {
    const confirmed = await window.confirm("Approve this attorney for facility access after completing verification?");
    if (!confirmed || actionId) return;
    await runAction(id, () => approveAttorneyAccess(id));
  };

  const handleReject = async (id) => {
    const reason = await window.appPrompt({ title: "Reject facility access", label: "Reason", message: "Provide a concise operational reason. Do not include privileged information.", confirmText: "Reject", variant: "danger" });
    if (!reason || actionId) return;
    await runAction(id, () => rejectAttorneyAccess(id, reason));
  };

  const handleRevoke = async (id) => {
    const reason = await window.appPrompt({ title: "Revoke facility access", label: "Reason", message: "Revocation also removes dependent client authorization. Do not include privileged information.", confirmText: "Revoke", variant: "danger" });
    if (!reason || actionId) return;
    await runAction(id, () => revokeAttorneyAccess(id, reason));
  };

  async function runAction(id, action) {
    setActionId(id);
    setError("");
    try {
      await action();
      await loadAccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Access Management</div>
          </div>
        </div>
       <AdminNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Attorney Access</h2>
            <p className="text-muted mb-0">
              Approve, reject, or revoke attorney access to your facility.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Requests</div>
            <h3 className="mb-0">{accesses.length}</h3>
          </div>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Facility Attorney Requests</h5>
              <div className="text-muted small">
                Pending requests appear first.
              </div>
            </div>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={loadAccess}
            >
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading attorney access...</p>
            ) : accesses.length === 0 ? (
              <div className="text-center py-5">
                <h5>No attorney access records</h5>
                <p className="text-muted mb-0">
                  Attorney facility requests will appear here.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Attorney</th>
                      <th>Law Firm</th>
                      <th>Facility</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {accesses.map((access) => {
                      const attorney =
                        access.attorney_profile?.user ||
                        access.attorneyProfile?.user;

                      const lawFirm =
                        access.attorney_profile?.law_firm ||
                        access.attorneyProfile?.lawFirm;

                      const facility = access.facility;

                      return (
                        <tr key={access.id}>
                          <td>
                            <strong>{attorney?.name || "Attorney"}</strong>
                            <div className="text-muted small">
                              {attorney?.email || "Attorney account"}
                            </div>
                          </td>

                          <td>{lawFirm?.name || "-"}</td>

                          <td>{facility?.name || "Assigned facility"}</td>

                          <td>
                            <span className={`status-pill status-${access.status}`}>
                              {access.status}
                            </span>
                          </td>

                          <td className="text-muted small">
                            {access.created_at || "-"}
                          </td>

                          <td className="text-end">
                            <div className="btn-group">
                              {access.status === "pending" && (
                                <>
                                  <AsyncButton
                                    className="btn btn-sm btn-success"
                                    busy={actionId === access.id}
                                    disabled={actionId !== null}
                                    busyText="Approving…"
                                    onClick={() => handleApprove(access.id)}
                                  >
                                    Approve
                                  </AsyncButton>

                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={actionId !== null}
                                    onClick={() => handleReject(access.id)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}

                              {access.status === "approved" && (
                                <AsyncButton
                                  className="btn btn-sm btn-outline-danger"
                                  busy={actionId === access.id}
                                  disabled={actionId !== null}
                                  busyText="Revoking…"
                                  onClick={() => handleRevoke(access.id)}
                                >
                                  Revoke
                                </AsyncButton>
                              )}

                              {["rejected", "revoked"].includes(access.status) && (
                                <AsyncButton
                                  className="btn btn-sm btn-outline-success"
                                  busy={actionId === access.id}
                                  disabled={actionId !== null}
                                  busyText="Approving…"
                                  onClick={() => handleApprove(access.id)}
                                >
                                  Re-approve
                                </AsyncButton>
                              )}

                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
